import { Box, Stack, SvgIcon, useTheme } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import useEmblaCarousel, { UseEmblaCarouselType } from 'embla-carousel-react';
import { useIsMobile } from '../../../hooks';
import { playIcon } from '../../../config/icons';
import { ECarouselVariant, TCarouselStore } from '../types';
import Thumb from './thumb';

type Props = Pick<TCarouselStore, 'variant'> & {
  slides: Array<{ url: string; isVideo: boolean }>;
  emblaMainApi?: UseEmblaCarouselType[1];
  mainImageUrl?: string;
};

export const Thumbnails = ({ slides, variant, emblaMainApi, mainImageUrl }: Props) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const direction = useTheme().direction;
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    dragFree: true,
    containScroll: 'keepSnaps',
    startIndex: 0,
    align: 'center',
    direction,
  });

  const isMobile = useIsMobile();

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return;
    const index = emblaMainApi.selectedScrollSnap();
    setSelectedIndex(index);
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
    emblaThumbsApi.scrollTo(index, true);
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaMainApi) return;
    onSelect();

    emblaMainApi.on('select', onSelect).on('reInit', onSelect);
  }, [emblaMainApi, onSelect, slides]);

  const isCompact = variant === ECarouselVariant.Compact;
  const isDefault = variant === ECarouselVariant.Default;
  const isExtended = variant === ECarouselVariant.Extended;

  const handleClick = useCallback(
    (index: number) => {
      emblaMainApi?.scrollTo(index);

      // we need to stop and restart autoplay if it's playing
      const autoplay = emblaMainApi?.plugins()?.autoplay;
      if (autoplay) {
        autoplay.stop();
        autoplay.play();
      }
    },
    [emblaMainApi],
  );

  return (
    <>
      {slides.length > 1 && (
        <Box
          ref={emblaThumbsRef}
          sx={{
            overflow: 'hidden',
            maxWidth: isDefault ? '100%' : { xs: '250px', sm: 'min(370px, 80%)' },
            zIndex: 2,
            color: 'white',
            width: '100%',
            position: isDefault ? 'relative' : 'absolute',

            ...(isExtended && {
              bottom: { xs: '15px', md: '20px' },
              left: { xs: '15px', md: '20px' },
            }),
          }}
        >
          <Stack direction="row" spacing={isExtended ? 1 : 0.5} justifyContent="flex-start">
            {slides.map((slide, index) => {
              const isActive = selectedIndex === index;
              const size = isCompact ? 35 : isExtended ? (isMobile ? 38 : 50) : 80;

              return slide.isVideo ? (
                <div
                  key={index}
                  onClick={() => emblaMainApi?.scrollTo(index)}
                  style={{ position: 'relative', height: size }}
                >
                  <Thumb
                    key={index}
                    src={mainImageUrl || slides[1]?.url}
                    onClick={() => handleClick(index)}
                    isActive={isActive}
                    size={size}
                  />
                  <SvgIcon
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 1,
                      color: 'white',
                    }}
                  >
                    {playIcon}
                  </SvgIcon>
                </div>
              ) : (
                <Thumb
                  key={index}
                  src={slide.url}
                  onClick={() => handleClick(index)}
                  isActive={isActive}
                  size={size}
                />
              );
            })}
          </Stack>
        </Box>
      )}
    </>
  );
};
